// in src/users.js
import React, { useCallback, useContext, useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { LinearProgress } from '@material-ui/core';
import { SelectInput, Datagrid, FormDataConsumer, required } from 'react-admin';
import { useFormState, useForm } from 'react-final-form';
import omit from 'lodash/omit';

import UserNotificationLookups from './UserNotificationLookups';
import { UserNotificationContext } from './UserNotificationContext';
import { buildId } from './UserNotificationCommon';

const useSelectFieldStyles = makeStyles({
  selectContainer: {
    margin: 0,
    '& > *:only-child': {
      border: 'none'
    }
  },
  progress: {
    marginLeft: 5,
    marginRight: 5
  }
});

const SelectField = props => {
  const {
    record,
    source,
    choices,
    disableWhen,
    formData,
    listIsLoading,
    cellClassName,
    dataLoading,
    ...rest
  } = props;
  const formState = useFormState();
  const form = useForm();
  const classes = useSelectFieldStyles();
  const { notificationContext } = useContext(UserNotificationContext);

  const choicesOverride = useMemo(() => {
    if (
      source === 'deliveryMethod' &&
      record.deliveryMethod !== 'EMAIL' &&
      record.deliveryMethod !== 'MAIL'
    ) {
      return UserNotificationLookups.deliveryMethodToDisplay;
    }
    return choices;
  }, [record, source, choices]);

  const handleChange = useCallback(
    evt => {
      //when changing Fund selection, we need to switch Investor selection to *
      let investorField =
        evt.target.name.substring(0, evt.target.name.length - 6) + 'investorId';
      form.change(investorField, '*');
      form.change(evt.target.name, evt.target.value);
    },
    [form]
  );

  if (disableWhen && disableWhen(record, formState.values)) return null;

  if (dataLoading || listIsLoading || notificationContext.saving) {
    return <LinearProgress classes={{ root: classes.progress }} />;
  }

  return (
    <SelectInput
      {...omit(rest, ['sortable'])}
      choices={choicesOverride}
      variant="standard"
      onChange={handleChange}
      source={`${record.id}.${source}`}
      classes={{ input: classes.selectContainer }}
      label=""
      options={{
        helperText: null
      }}
    />
  );
};

const useStyles = makeStyles({
  cell: {
    padding: '0 !important'
  },
  row: {
    backgroundColor: 'white !important'
  }
});

const unicity = () => (value, allValues, props) => {
  const ids = {};
  for (const val of Object.values(allValues)) {
    const id = buildId(val);
    if (ids[id] && props.name && props.name.startsWith(id + '.')) {
      return 'duplicate Notification.';
    }
    ids[id] = true;
  }
  return undefined;
};

const validateUnicity = [unicity()];

const sort = { field: 'id', order: 'ASC' };

const UserNotificationDatagrid = props => {
  const {
    notifications = [],
    documentTypes,
    userAddressTypesAvailable,
    userAddressTypes,
    fundChoices,
    investorChoices,
    data,
    fundInvestors,
    fundsLoading,
    investorsLoading,
    fundInvestorsLoading,
    userId,
    loading,
    ...rest
  } = props;
  const classes = useStyles(props);

  const handleDisableParameter = useCallback((record, formState) => {
    const newRecord = formState[record.id] || {};
    return newRecord.deliveryMethod !== 'MAIL';
  }, []);

  const notIds = useMemo(() => notifications.map(({ id }) => id), [
    notifications
  ]);

  return (
    <Datagrid
      {...omit(rest, ['saving', 'hasWriteUserNotificationPermission'])}
      data={data}
      ids={notIds}
      currentSort={sort}
      className={classes.fullWidth}
      classes={{ row: classes.row }}
      loading={loading}
    >
      <SelectField
        source="fundId"
        label="Fund"
        emptyText="All Funds"
        emptyValue="*"
        allowEmpty
        choices={fundChoices}
        cellClassName={classes.cell}
        sortable={false}
        listIsLoading={loading}
        dataLoading={fundsLoading && fundInvestorsLoading}
        validate={validateUnicity}
        disabled={fundInvestorsLoading}
        id="fund"
      />
      <FormDataConsumer label="Investor" cellClassName={classes.cell}>
        {formState => {
          const { record, formData } = formState;
          const currentRecord = Object.values(formData).find(
            item => item.id === record.id
          );
          let choices = investorChoices;
          if (currentRecord && currentRecord.fundId !== '*') {
            choices = fundInvestors[currentRecord.fundId] || [];
          }

          return (
            <SelectField
              {...formState}
              source="investorId"
              emptyText="All Investors"
              emptyValue="*"
              allowEmpty
              choices={choices}
              sortable={false}
              listIsLoading={loading}
              dataLoading={investorsLoading && fundInvestorsLoading}
              validate={validateUnicity}
              disabled={fundInvestorsLoading}
              id="investor"
            />
          );
        }}
      </FormDataConsumer>
      <SelectField
        variant="standard"
        source="documentTypeId"
        choices={documentTypes}
        label="Document Type"
        optionText="description"
        options={{
          helperText: null
        }}
        cellClassName={classes.cell}
        sortable={false}
        listIsLoading={loading}
        dataLoading={false}
        validate={validateUnicity}
        id="document-type"
      />
      <FormDataConsumer label="Delivery Method">
        {formState => {
          const { record } = formState;
          const disabled =
            record.deliveryMethod !== 'EMAIL' &&
            record.deliveryMethod !== 'MAIL';
          return (
            <SelectField
              {...formState}
              source="deliveryMethod"
              choices={UserNotificationLookups.deliveryMethod}
              cellClassName={classes.cell}
              sortable={false}
              listIsLoading={loading}
              dataLoading={false}
              disabled={disabled}
              validate={validateUnicity}
              id="delivery-method"
            />
          );
        }}
      </FormDataConsumer>
      <FormDataConsumer label="Parameter">
        {formState => {
          const { record, formData } = formState;
          const currentRecord = Object.values(formData).find(
            item => item.id === record.id
          );
          let choices = [];
          if (currentRecord) {
            if (currentRecord.deliveryMethod === 'MAIL') {
              let uatFound = false;
              choices = userAddressTypesAvailable.map(item => {
                if (currentRecord.parameter === item.id.toString()) {
                  uatFound = true;
                }
                return {
                  id: item.id,
                  name: item.name
                };
              });
              if (currentRecord.parameter && !uatFound) {
                const uat = userAddressTypes.find(
                  item => item.id.toString() === currentRecord.parameter
                );
                if (uat) {
                  choices.push(uat);
                }
              }
            } else if (currentRecord.parameter) {
              currentRecord.parameter = '';
            }
          }

          return (
            <SelectField
              {...formState}
              source="parameter"
              choices={choices}
              disableWhen={handleDisableParameter}
              cellClassName={classes.cell}
              sortable={false}
              listIsLoading={loading}
              dataLoading={false}
              validate={[required(), unicity()]}
              allowEmpty={false}
              id="parameter"
            />
          );
        }}
      </FormDataConsumer>
    </Datagrid>
  );
};

export default UserNotificationDatagrid;
